import { Delete as DeleteIcon } from "@mui/icons-material";
import { Box, IconButton, TableRow, Typography } from "@mui/material";
import CustomTable from "../../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../../shared/components/CustomTable/Styled";
import TextBox from "../../../../../shared/components/TextBox";
import { color } from "../../../../../shared/styles";
import { formatInternationalNumber, formatNumber, parseInternationalToNumber } from "../../../../../utils/format";
import { error } from "../../../../../utils/toast";
import { getMaterialRequestStock } from "../stock";

interface MaterialExportDetailsProps {
    materials: any[];
    onChangeExportQuantity: (id: string, quantity: number) => void;
    onChangeExportNote: (id: string, note: string) => void;
    onChangeMaterialRequestedQuantity?: (id: string, quantity: number) => void;
    removeSelectedItem?: (item: any) => void;
}

const TableColumns = [
    { text: "Mã vật tư", icon: null },
    { text: "Tên vật tư", icon: null },
    { text: "Số lượng yêu cầu", icon: null },
    { text: "Số lượng khả dụng", icon: null },
    { text: "Số lượng hết hạn", icon: null },
    { text: "Số lượng xuất (ĐVC)", icon: null },
    { text: "Đơn vị chính", icon: null },
    { text: "Số lượng chuyển đổi (ĐVP)", icon: null },
    { text: "Đơn vị phụ", icon: null },
    { text: "", icon: null },
];

const MaterialRow = ({ item, extraRowProps }: { item: any; extraRowProps: any }) => {
    // Guard against undefined item
    if (!item) {
        return null;
    }

    // Helper functions
    const roundTo5Decimal = (value: number): number => {
        if (!Number.isFinite(value)) return 0;
        return Math.round((value + Number.EPSILON) * 100000) / 100000;
    };

    const getRequestQuantity = (item: any) => item.request_quantity || 0;
    const getConvertedQuantity = (item: any) => {
        const exportQuantity = item.exportQuantity || 0;
        return roundTo5Decimal(exportQuantity / (item.conversion_rate || 1));
    };
    // Variables
    const materialCode = item.material_code || item.id || "N/A";
    const stock = getMaterialRequestStock(item);
    const expiredDifference = (item.exportQuantity || 0) - stock.available;
    // The stock service does not consume expired quantities at or below 1e-9.
    const expiredUsage = expiredDifference > 1e-9 ? expiredDifference : 0;
    const isQuantityExceeded = (item.exportQuantity || 0) > stock.total
        || (item.exportQuantity || 0) > (getRequestQuantity(item) - (item.exported_quantity || 0));
    const selectedQuantityBorderColor = isQuantityExceeded ? color.status.FAIL
        : expiredUsage > 0 ? color.status.WARNING : undefined;
    const backgroundColor = isQuantityExceeded ? color.status.DANGER_LIGHT
        : expiredUsage > 0 ? color.status.WARNING_LIGHT : "transparent";


    return (
        <TableRow sx={{
            backgroundColor,
        }}>
            <StyledTableCell align="left">{materialCode}</StyledTableCell>
            <StyledTableCell align="left">{item.name}</StyledTableCell>
            <StyledTableCell
                align="left">{formatNumber(getRequestQuantity(item) - item.exported_quantity)}</StyledTableCell>
            <StyledTableCell align="left">{formatNumber(stock.available)} / {formatNumber(stock.total)}</StyledTableCell>
            <StyledTableCell align="left">{formatNumber(stock.expired)} / {formatNumber(stock.total)}</StyledTableCell>
            <StyledTableCell align="left">
                <TextBox
                    numeric
                    style={{
                        borderColor: selectedQuantityBorderColor,
                        backgroundColor,
                    }}
                    text={formatInternationalNumber(item.exportQuantity ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeMaterialRequestedQuantity(item.id, n);
                    }}
                />
                {expiredUsage > 0 && (
                    <Typography component="div" role="alert" variant="body2" sx={{ mt: 1 }}>
                        Lần xuất này sẽ sử dụng {formatNumber(expiredUsage)} {item.measurement_unit} vật tư hết hạn.
                    </Typography>
                )}
            </StyledTableCell>
            <StyledTableCell align="left">{item.measurement_unit}</StyledTableCell>
            <StyledTableCell align="left">
                <TextBox
                    numeric
                    style={{
                        borderColor: selectedQuantityBorderColor,
                        backgroundColor,
                    }}
                    text={formatInternationalNumber(getConvertedQuantity(item) ?? "")}
                    onChangeText={(value: string) => {
                        const n = parseInternationalToNumber(value, error);
                        if (!Number.isFinite(n)) return;
                        extraRowProps.onChangeMaterialRequestedQuantity(
                            item.id,
                            roundTo5Decimal(n * (item.conversion_rate || 1)),
                        );
                    }}
                />
            </StyledTableCell>
            <StyledTableCell align="left">{item.secondary_measurement_unit || ""}</StyledTableCell>
            <StyledTableCell align="left">
                <IconButton onClick={() => extraRowProps.removeSelectedItem(item)}>
                    <DeleteIcon />
                </IconButton>
            </StyledTableCell>
        </TableRow>
    );
};

export const MaterialExportDetails = ({
                                          materials,
                                          onChangeExportQuantity,
                                          onChangeExportNote,
                                          onChangeMaterialRequestedQuantity,
                                          removeSelectedItem,
                                      }: MaterialExportDetailsProps) => {
    return (
        <Box sx={{ width: "100%", mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Danh sách vật tư từ yêu cầu
            </Typography>

            <CustomTable
                itemList={materials}
                TableColumns={TableColumns}
                RowElement={MaterialRow}
                extraRowProps={{
                    onChangeExportQuantity,
                    onChangeExportNote,
                    onChangeMaterialRequestedQuantity,
                    removeSelectedItem,
                }}
            />
        </Box>
    );
};
